/**
 * Every tunable in one place. Units: world units (wu), seconds, wu/s.
 * The room is 32 x 18 wu, centered on the origin.
 */
export const Config = {
  sim: {
    tickHz: 120,
    maxStepsPerFrame: 8,
    maxEntities: 4096,
  },
  room: {
    width: 32,
    height: 18,
    wallThickness: 0.6,
    gateWidth: 3.2,
    cornerRegionSize: 3.5,
    enterDuration: 1.2,
    clearDuration: 2.0,
    /** Smash TV walk: camera slides one room over while the wizard crosses the door. */
    walkSeconds: 1.35,
  },
  pools: {
    imps: 600,
    golems: 40,
    projectiles: 2000,
    pickups: 32,
    decals: 700,
    spawners: 8,
    fx: 640,
    particles: 6000,
    /** Wall guns: a handful per room, own cap so they never steal swarm slots. */
    gargoyles: 8,
    /** Floor bosses: one live at a time plus one still playing its death. */
    bosses: 2,
  },
  player: {
    /** Collision / contact radius — kept tight so a larger sprite still feels responsive. */
    radius: 0.46,
    /** Rendered body scale (world units tall at atlas ppu). ~23% above the old 1.3. */
    spriteScale: 1.6,
    maxHealth: 100,
    moveSpeed: 8.5,
    /**
     * Double-tap a cardinal (WASD / arrows / d-pad) or flick the move stick
     * the same way twice. A short burst, then a cooldown so it stays a dodge.
     */
    dash: {
      window: 0.26,
      cooldown: 0.4,
      duration: 0.13,
      speed: 30,
      invuln: 0.1,
      /** Stick must pass this to count as a tap; diagonals are ignored. */
      stickTap: 0.62,
    },
    deadzone: 0.08,
    accelTime: 0.05,
    slideTime: 0.08,
    backpedalPenalty: 0.12,
    aimMagnetismDeg: 5,
    magnetRadius: 2.2,
    invulnAfterHit: 0.5,
    /** I-frames after spending a continue so the pile-up does not eat the next life. */
    continueInvuln: 2,
    contactDamage: 6,
    /** Low power warning threshold (starts visual/audio alerts sooner). */
    lowPowerFraction: 0.4,
    /** Critical threshold (intensifies alarms, flashing, and heartbeat). */
    criticalPowerFraction: 0.2,
  },
  enemies: {
    /**
     * Imps peel around a wide ring, fake a sidestep, then telegraph a short lunge.
     * spread is the owned spot on that ring (wu). Body contact stays 2 x collision.body.imp.
     */
    imp: {
      hp: 2,
      speed: 4.6,
      radius: 0.28,
      separation: 1.15,
      encircle: 0.95,
      spread: 1.35,
      lungeSpeed: 8.6,
      lungeSeconds: 0.15,
      windSeconds: 0.18,
      sidestepSeconds: 0.26,
      score: 10,
      gemChance: 0.08,
    },
    /**
     * Golems walk, then wind up a readable stomp when close. The ring is a tell;
     * contact during the slam is the hit. They still plow — crowdSlide does not apply.
     */
    golem: {
      hp: 40,
      speed: 1.9,
      radius: 0.62,
      knockbackResist: 0.85,
      stompRange: 2.2,
      stompWind: 0.42,
      stompSeconds: 0.2,
      stompSpeed: 4.8,
      stompRest: 1.8,
      score: 120,
      prizeChance: 0.5,
      heartChance: 0.25,
    },
    /** Skulls hover, then commit a longer screaming dash with a short lead. Debut is tier 2. */
    skull: {
      hp: 1,
      speed: 3.2,
      lungeSpeed: 14,
      lungeSeconds: 0.38,
      restSeconds: 0.5,
      lead: 0.22,
      radius: 0.26,
      score: 15,
      minTier: 2,
    },
    /**
     * Slimes hop on their own beat toward a slightly led point, heading locked at launch.
     * Debut is the tier-3 stretch.
     */
    slime: {
      hp: 7,
      hopSpeed: 6.5,
      hopSeconds: 0.43,
      squatSeconds: 0.5,
      hopLead: 0.18,
      spread: 0.8,
      radius: 0.34,
      separation: 1.45,
      score: 25,
      heartChance: 0.08,
      minTier: 3,
    },
    /**
     * Bats hold different orbit radii, then dive through an offset near the wizard.
     * Debut is the tier-4 stretch.
     */
    bat: {
      hp: 2,
      speed: 6.5,
      orbitRadius: 4.2,
      diveSpeed: 12,
      diveSeconds: 0.5,
      orbitSeconds: 1.6,
      diveOffset: 1.45,
      diveLead: 0.12,
      radius: 0.28,
      score: 20,
      minTier: 4,
    },
    /**
     * Wall gargoyles: planted turrets. Combat debut is tier 3 (after imps, with slimes).
     * Hazard pits can show them a room earlier so the room type has a tell.
     */
    gargoyle: {
      hp: 14,
      radius: 0.42,
      score: 60,
      minTier: 3,
      hazardMinTier: 2,
      telegraph: 0.55,
      fireInterval: 1.85,
      boltSpeed: 10.5,
      boltDamage: 8,
      boltLife: 1.4,
      boltRadius: 0.16,
      knockback: 3.2,
      gemChance: 0.18,
    },
    /**
     * Acid spitters: the gargoyle's meaner cousin. Slower cadence, a three-bolt fan you
     * have to read instead of sidestep. Mixed into the wall-gun budget from spitterFromTier.
     */
    spitter: {
      hp: 18,
      radius: 0.42,
      score: 90,
      minTier: 5,
      telegraph: 0.72,
      fireInterval: 2.6,
      boltSpeed: 9,
      boltDamage: 6,
      boltLife: 1.5,
      boltRadius: 0.17,
      knockback: 2.4,
      /** Fan: this many bolts across this total angle (degrees). */
      fanCount: 3,
      fanDegrees: 26,
      gemChance: 0.3,
      /** Share of a room's wall guns that become spitters once unlocked (gauntlets use gauntletShare). */
      share: 0.4,
      gauntletShare: 0.65,
    },
    /**
     * Imp hexers hang back at kiteRange, strafe, and lob a telegraphed hex bolt. The first
     * small fry that shoots. Debut is the tier-5 stretch.
     */
    hexer: {
      hp: 4,
      speed: 3.6,
      radius: 0.3,
      kiteRange: 6.5,
      kiteSlack: 1.6,
      castInterval: 2.4,
      telegraph: 0.5,
      boltSpeed: 8.2,
      boltDamage: 6,
      boltLife: 1.6,
      boltRadius: 0.16,
      /** From this tier a hexer fires two bolts in a narrow fan. */
      twinFromTier: 8,
      score: 30,
      gemChance: 0.15,
      minTier: 5,
    },
    /**
     * Cinder skulls chase in a straight line and burst on contact or death: a scorched
     * radius that also cooks nearby fiends. Debut is the tier-6 stretch.
     */
    cinder: {
      hp: 3,
      speed: 4.4,
      /** Speed ramps up over this many seconds of chasing. */
      rampSeconds: 2.2,
      rampMax: 1.55,
      radius: 0.3,
      blastRadius: 1.9,
      blastDamage: 11,
      /** Damage to other fiends caught in the blast (chains cinders). */
      blastFriendly: 7,
      blastKnockback: 9,
      score: 20,
      minTier: 6,
    },
    /**
     * Blight slimes: a heavy, slow hopper that pops into two hex slimes.
     * Debut is the tier-7 stretch.
     */
    blight: {
      hp: 16,
      hopSpeed: 5.4,
      hopSeconds: 0.46,
      squatSeconds: 0.78,
      hopLead: 0.2,
      spread: 1.1,
      radius: 0.5,
      separation: 1.8,
      splitCount: 2,
      score: 55,
      heartChance: 0.14,
      minTier: 7,
    },
    /**
     * Hollow wraiths drift, fade out, reform a few paces behind the wizard and lunge.
     * They cannot be hit while faded. Debut is the tier-8 stretch (earlier on Haunted floors).
     */
    wraith: {
      hp: 3,
      speed: 2.6,
      radius: 0.3,
      driftSeconds: 2.6,
      fadeSeconds: 0.42,
      reformSeconds: 0.55,
      lungeSpeed: 9.5,
      lungeSeconds: 0.32,
      /** Reform this far from the wizard, on the side away from where the wraith was. */
      flankRange: 3.4,
      score: 35,
      minTier: 8,
    },
    /**
     * Ember brutes: golem frame, none of the patience. They walk, lock a heading, then
     * charge until they hit a wall or the wizard. Dazed after a wall hit.
     * Portals cough one instead of a golem once bruteFromTier is reached (every other heavy).
     */
    brute: {
      hp: 24,
      speed: 2.5,
      radius: 0.56,
      knockbackResist: 0.7,
      chargeRange: 8.5,
      chargeWind: 0.55,
      chargeSpeed: 11.5,
      chargeSeconds: 1.1,
      chargeRest: 1.4,
      dazedSeconds: 1.25,
      /** Lead on the wizard's velocity when locking the charge heading. */
      lead: 0.25,
      score: 150,
      prizeChance: 0.35,
      heartChance: 0.2,
      minTier: 5,
    },
    /**
     * Mimics pose as gold chests. Waking one (walk up, or shoot it) turns it into a fast
     * hopper with a chest full of gems. Treasure vaults get one or two; combat rooms rarely.
     */
    mimic: {
      hp: 20,
      radius: 0.5,
      wakeRange: 2.7,
      wakeSeconds: 0.5,
      hopSpeed: 8.4,
      hopSeconds: 0.32,
      squatSeconds: 0.36,
      hopLead: 0.12,
      score: 200,
      gems: 5,
      prizeChance: 0.5,
      minTier: 3,
      /** Second vault mimic from this tier. */
      twinFromTier: 6,
      /** Chance a combat room from minTierCombat hides one among the floor. */
      combatChance: 0.15,
      minTierCombat: 5,
    },
    /**
     * Portal bursts are one kind. Featured weight is the chance a burst is the room's star.
     * Hazard leans on the newest guest; treasure stays light. After every guest has debuted,
     * combat/exit ease the newest-kind bias toward featuredLate so later floors mix.
     */
    spawnMix: {
      featuredCombat: 0.72,
      featuredExit: 0.66,
      featuredHazard: 0.88,
      featuredTreasure: 0.36,
      leftoverImpShare: 0.55,
      featuredLate: 0.52,
      featuredLateFromTier: 6,
    },
    /**
     * Portal cadence. Concurrent small-fry ceiling lives on Difficulty.liveCap —
     * do not raise burst size or this interval to "fill" a room already at cap.
     */
    spawner: {
      hp: 60,
      radius: 0.85,
      baseInterval: 1.0,
      minInterval: 0.28,
      golemEvery: 14,
      /** Elite floors cough a heavy this often instead. */
      golemEveryElite: 8,
      score: 400,
      /** Burst pack lands on this ring (wu from portal center), not one shared point. */
      burstRingMin: 1.2,
      burstRingMax: 1.9,
      /** Outward wu/s on spawn so the pack leaves the mouth instead of sitting in it. */
      burstPop: 3.6,
    },
  },
  /**
   * Soft enemy-enemy body resolve (not steering). Split the overlap, cancel closing
   * speed. A few iterations plus a real push cap open a portal pile; the cap is still
   * small enough that a 200-imp wall slides instead of exploding.
   */
  collision: {
    relax: 0.58,
    slop: 0.02,
    damp: 0.85,
    /** Per-iteration cap. Two skull radii (0.52) clear in a couple of ticks at 3 iters. */
    maxPush: 0.3,
    iterations: 3,
    /** Drop this fraction of velocity along a contact normal (1 = keep tangent only). */
    crowdSlide: 1,
    /** Start the crowd slide this far before hard contact. */
    crowdPad: 0.1,
    mass: { imp: 1, golem: 8, skull: 0.75, slime: 1.45, bat: 0.7, gargoyle: 40, boss: 400, wraith: 0.6, hexer: 1, cinder: 0.8, blight: 2.6, brute: 6, mimic: 3 },
    /**
     * Enemy-vs-enemy body radius (wu), sized to the drawn sprite footprint. The
     * enemies.*.radius values are hit radii and only cover ~60% of the art, so bodies
     * "touching" at hit radius overlapped a third of their sprite width on screen.
     * Kinds not listed fall back to their hit radius.
     */
    body: { imp: 0.4, golem: 0.9, skull: 0.45, slime: 0.55, bat: 0.48, wraith: 0.45, hexer: 0.42, cinder: 0.46, blight: 0.78, brute: 0.82, mimic: 0.62 },
    /** Collide-radius scale when both are airborne (own kind must not share a spot). */
    flyerSame: 0.98,
    /** Flyer radius scale vs grounded. Near 1 so skulls cannot sit inside imps/golems. */
    flyerOver: 0.94,
  },
  dragon: {
    stallSeconds: 34,
    cornerSeconds: 7,
    warningLead: 6,
    baseSpeed: 2.6,
    accel: 0.35,
    maxSpeed: 10,
    radius: 0.95,
    /** Sprite scale: body length in world units (the dorsal art is fit to its length). */
    scale: 6.2,
    /** Body slam. Two hits hurt; five kill. Invuln after each gives time to peel off. */
    damage: 22,
    breathInterval: 5.4,
    /** First breath waits this long after the fly-in finishes. */
    firstBreathDelay: 2.2,
    breathWindup: 0.95,
    breathConeDeg: 28,
    /** Seconds the flame jet streams for; flames are spawned across this window. */
    breathDuration: 0.4,
    /** Total flame projectiles per breath, spread across breathDuration. */
    breathCount: 7,
    flameSpeed: 9,
    flameLife: 0.75,
    flameDamage: 7,
    /** Radians per second the dragon can turn while hunting. */
    turnRate: 2.4,
  },
  /**
   * Floor bosses. The exit room's portals are the warm-up; once they and the small
   * fry are gone the floor's archmage arrives and the room only clears when it dies.
   * Every knob here is the floor 1 value; `perFloor` fields scale it per extra floor.
   */
  boss: {
    radius: 1.05,
    /** Sprite scale (world units tall for the reference frame). */
    scale: 2.9,
    hp: 520,
    hpPerFloor: 0.35,
    score: 2500,
    /** Seconds of the entrance ritual; invulnerable for all of it. */
    arriveSeconds: 1.7,
    /** Death crescendo length before the room clears. */
    deathSeconds: 2.4,
    contactDamage: 14,
    /** Base projectile speed and how much each floor adds (capped). */
    boltSpeed: 7.6,
    boltSpeedPerFloor: 0.7,
    boltSpeedMax: 12.5,
    boltDamage: 8,
    boltDamagePerFloor: 1.5,
    boltDamageMax: 18,
    /** Idle gap between patterns; shrinks per floor down to the floor value. */
    restSeconds: 1.5,
    restPerFloor: 0.18,
    restMin: 0.55,
    /** Below this hp fraction the boss enrages: shorter rests, extra volley. */
    enrageBelow: 0.5,
    enrageRestMult: 0.75,
    /** Drift speed while hovering and the standoff distance it tries to keep. */
    hoverSpeed: 1.6,
    standoff: 6.5,
    /** Blink away when a wizard gets this close (seconds of windup first). */
    blinkRange: 3.2,
    blinkWindup: 0.35,
    blinkCooldown: 4,
    /** Rune points the boss blinks between (room is 32 x 18). */
    anchors: [
      [0, 0],
      [-8, 3.5],
      [8, 3.5],
      [-8, -3.5],
      [8, -3.5],
      [0, 5],
      [0, -5],
    ],
    /**
     * Room champions: the quicker bosses that guard every non-exit fight room.
     * Tuned so the room-1 champion dies in roughly 15-25 s on Adept with the wand plus a drop.
     */
    champion: {
      hp: 190,
      /** HP growth per global room index beyond the first, and the cap on that growth. */
      hpPerRoom: 0.09,
      hpGrowthMax: 3.4,
      /** Treasure rooms get a lighter guardian. */
      treasureHpMult: 0.6,
      /** Champion's Ring: no portals, so the duel itself carries the room. */
      arenaHpMult: 1.3,
      score: 900,
      arriveSeconds: 1.9,
      deathSeconds: 2.2,
      contactDamage: 12,
      /** Champion rests: longer than the archmage early on so room 1 reads as fair. */
      restSeconds: 1.7,
      restPerRoom: 0.06,
      restMin: 0.6,
      /** Rumble build between the last portal falling and the boss appearing. */
      buildSeconds: 1.0,
    },
    /** Every fight room escalates its guardian: pattern unlocks at these global room indices. */
    escalation: {
      /** Third pattern unlocks here. */
      tier2Room: 4,
      /** Fourth pattern / enrage summons unlock here. */
      tier3Room: 9,
    },
  },
  anim: {
    /** Velocity (wu/s) at which the run clip plays at 1x speed. */
    runRefSpeed: { player: 8.5, imp: 4.6, golem: 1.9, brute: 2.5 },
    bobAmp: 0.05,
    leanPerVelocity: 0.02,
    squashDecay: 7,
    popDecay: 5,
    hurtSeconds: 0.25,
    /** Hit flash fade rate (1/s) and how far a jolt shoves the sprite per unit of knockback. */
    flashDecay: 14,
    joltDecay: 11,
    joltPerKnockback: 0.045,
    /** Big-enemy stagger: seconds the golem flinches after a hit and its speed while doing so. */
    golemStagger: 0.32,
    golemStaggerSpeed: 0.25,
    /** Wizard presentation: stride bounce, lean, robe trail, cast snap. */
    player: {
      bobAmp: 0.09,
      leanPerVelocity: 0.032,
      maxLean: 0.32,
      strideHz: 11,
      strideSquash: 0.28,
      trailMinSpeed: 2.2,
      trailSpacing: 0.2,
      castPull: 0.2,
      castThrust: 0.18,
      muzzleReach: 0.95,
      faceSnapSquash: 0.55,
      footstepSquash: 0.4,
    },
  },
  pickups: {
    weaponLife: 12,
    gemLife: 9,
    prizeLife: 10,
    overchargeSeconds: 10,
    weaponRadius: 0.5,
    gemRadius: 0.28,
    gemMagnetSpeed: 12,
    /** Rune hearts: hp restored, how long they linger, drop odds off small fry. */
    heartHeal: 25,
    heartLife: 11,
    impHeartChance: 0.012,
    /** Supply drops: Epic Weapons drift in through a gate mid-fight on this cadence. */
    supplyFirstSeconds: 9,
    supplyIntervalSeconds: 16,
    /** A heart drifts in instead when the player is below this hp fraction (and none is out). */
    supplyHeartBelow: 0.4,
    supplyHeartSeconds: 11,
    /** Drift speed through the gate; MovementSystem drag brings it to rest inside. */
    supplyDriftSpeed: 5.5,
    /**
     * Cataclysm Orb: a one-shot room wipe, excluded from the regular drop pool
     * and from boss loot (the room is already empty). These chances are the
     * only way it shows up in a normal run.
     */
    cataclysmSupplyChance: 0.16,
    cataclysmSanctumChance: 0.42,
    cataclysmTreasureChance: 0.28,
    cataclysmSpawnerChance: 0.1,
    cataclysmArenaChance: 0.22,
    /**
     * Rune Ward: off-hand swirling shield. Does not replace the held weapon.
     * Chip nearby trash; not a room wipe (that's Cataclysm).
     */
    shield: {
      duration: 22,
      blades: 3,
      orbitRadius: 1.42,
      spin: 3.9,
      bladeRadius: 0.3,
      damage: 2.5,
      knockback: 3.4,
      hitCooldown: 0.36,
      targetIframe: 0.5,
      spawnerMult: 0.35,
      tint: 0x4ef0c8,
      pickupTint: 0x5cffd8,
      hitSfxGap: 0.16,
    },
    /** Gale Step: timed move-speed perk. Stacks with the ward. */
    swift: {
      duration: 9,
      speedMult: 1.42,
      tint: 0xffe06a,
      pickupTint: 0xffd23a,
    },
    /** Mid-fight bonus cadence for ward / gale (separate from Epic Weapons). */
    bonusFirstSeconds: 11,
    bonusIntervalSeconds: 20,
    /** Extra bonus on a dead portal (weapon still drops). */
    bonusPortalChance: 0.22,
    /** Prize-wave toss becomes a bonus instead of prize/gem. */
    bonusWaveChance: 0.1,
  },
  /**
   * Floor mines. Planted in every fight room. Player and enemy footsteps (and
   * stray shots) light a short fuse, then a wide blast. Stay off gates and the
   * entry tile so the room is still pathable.
   */
  mines: {
    countMin: 2,
    countBase: 2,
    /** Extra mine every this many tiers after the first. */
    countEveryTiers: 3,
    /** Extra mines per floor index (floor 0 = first floor). */
    countPerFloor: 1,
    countMax: 6,
    hazardBonus: 2,
    gauntletBonus: 1,
    radius: 0.4,
    blastRadius: 2.35,
    /** Major: about 38% of the wizard's bar on Adept before difficulty. */
    playerDamage: 38,
    /** Kills most small fry; golems and heavies live through one. */
    enemyDamage: 32,
    knockback: 11,
    fuseSeconds: 0.3,
    minSpacing: 3.6,
    entryClear: 4.4,
    portalClear: 2.8,
  },
  fx: {
    hitStopMs: 70,
    spawnerShake: 0.55,
    killShake: 0.06,
    shakeDecay: 6,
    rumbleMs: 160,
  },
  audio: {
    duckAttackMs: 20,
    duckReleaseMs: 250,
    sfxDuckDb: -12,
    musicDuckDb: -6,
    subBassCents: -1200,
    subBassGain: 0.35,
    /** Silence between Vaelen lines so the next clip never stacks on the last tail. */
    voiceBreathMs: 480,
    /** Extra quiet after any line before flavor chatter (hits, pickups, heckles) may start. */
    voiceChatterGapMs: 4200,
  },
} as const;
